"use client";

import { ErrorCard } from "@/components/ui/shared/ErrorCard";
import { useErrorAutoRetry } from "@/hooks/useErrorAutoRetry";
import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";

/**
 * Global error component for the app.
 *
 * @param error - The error object
 * @param reset - The function to reset the error
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  // Silently retry once for transient (e.g. cold-start) failures before showing the card.
  // global-error replaces the whole document, so use inline styles (no app CSS guaranteed)
  // and a neutral, announced placeholder instead of a blank page.
  const isRetrying = useErrorAutoRetry(error, reset);
  if (isRetrying) {
    return (
      <html lang="en">
        <body
          style={{
            margin: 0,
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "system-ui, sans-serif",
            color: "#4b5563",
          }}
        >
          <div role="status" aria-live="polite">
            Loading…
          </div>
        </body>
      </html>
    );
  }

  return (
    <html>
      <body>
        <ErrorCard
          title="Something went wrong"
          message="Don't worry! Our friendly pup is on the case. Try again or head back home while we fetch a solution."
          sparkleType="hearts"
          showBackButton={false}
          showHomeButton={true}
          showRetryButton={true}
          onRetry={reset}
          helpText="Still having issues?"
        />
      </body>
    </html>
  );
}
